import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { StepsNavigationLegacy } from '@/widgets/ServiceFormTemplate';

import { MergedServiceCodes } from '@/shared/types';
import { ConditionView } from '@/shared/ui';

import { ServiceFeedback } from '@/components/Forms/ServiceFeedback';

import { SERVICE_CODES } from '@/lib/consts';

import { allAllowedToChoosePassportDocumentTypeCodes } from '../../../../../components/Forms/constants';
import { HeaderWithHomeLinkButton } from '../../../../../components/Forms/HeaderWithHomeLinkButton';
import { ServiceInformer } from '../../../../../components/Forms/ServiceInformer';
import { ServiceInformerContainer } from '../../../../../components/Forms/ServiceInformer/ServiceInformerContainer';
import { ServiceInformerType } from '../../../../../components/Forms/ServiceInformer/types';
import { ServiceNames, StepsNavigationName } from '../../../../../config/services';
import { getDateWithoutTime } from '../../../../../helpers/dates';
import { StepNavigation, stepsNavigationDataFactory } from '../../../../../helpers/stepsNavigationDataFactory';

import FormSteps from './Steps/StepInitial';

import '../../../../../containers/StyleSettings/themes/default/layout.scss';
import './TemporaryResidenceRelocation.scss';

type ResidenceRelocationServiceCode =
  | MergedServiceCodes['RESIDENCE_RELOCATION']
  | MergedServiceCodes['TEMPORARY_RESIDENCE_RELOCATION'];
export let currentServiceCode: Nullable<ResidenceRelocationServiceCode> = null;

export let serviceName: string;
export let totalSteps: number;
export let stepNumberToSubmitForm: number;
export let stepNumberToDownloadPdf: number;

export const todaysDate = getDateWithoutTime(new Date());
export const oneDayFromToday = new Date(todaysDate);
export const sixMonthsFromToday = new Date(todaysDate);
oneDayFromToday.setDate(todaysDate.getDate() + 1);
sixMonthsFromToday.setMonth(todaysDate.getMonth() + 6);

interface Props {
  serviceCode: ResidenceRelocationServiceCode;
}

export const disallowedDocumentTypeCodesForAdditionalDocumentStep = [...allAllowedToChoosePassportDocumentTypeCodes];

export const TemporaryResidenceRelocation: React.FC<Props> = ({ serviceCode }) => {
  const htmlRef = useRef<HTMLElement>(null);
  const [myObjects, setMyObjects] = useState<StepNavigation[]>([]);
  const currentStep = useMemo(() => myObjects.find((step) => step.active), [myObjects]);
  const isLastStep: boolean = currentStep === myObjects[myObjects.length - 1];
  const [currentRequestId, setCurrentRequestId] = useState<string>('');
  const isClueInformerActive = useMemo(
    () => currentStep?.step === myObjects[0]?.step || currentStep?.step === myObjects.at(-1)?.step,
    [currentStep, myObjects],
  );
  const isValidityPeriodViolationInformerActive = useMemo(() => currentStep?.step === 2, [currentStep]);
  const isPlaceOfResidenceInformerActive = useMemo(() => currentStep?.step === 4, [currentStep]);
  const isQrCodeInformerActive = useMemo(() => currentStep?.step === 8, [currentStep]);

  const handleReceiveRequestId = (requestId: string): void => {
    setCurrentRequestId(requestId);
  };

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const stepToActivate = Number(htmlRef.current?.querySelector('div[data-step]')?.getAttribute('data-step'));
      const newData = JSON.parse(JSON.stringify(myObjects)).map((step: StepNavigation) => {
        if (step.step === stepToActivate) {
          step.active = true;
          step.completed = false;
        } else {
          if (step.step < stepToActivate) {
            step.completed = true;
          }
          if (step.active) {
            step.active = false;
          }
        }

        return step;
      });

      if (isEqual(newData, myObjects)) return;

      setMyObjects(newData);
    });
    htmlRef.current && observer.observe(htmlRef.current, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [myObjects]);

  useEffect(() => {
    currentServiceCode = serviceCode;
    serviceName = ServiceNames[serviceCode];

    const navigationData = stepsNavigationDataFactory([
      StepsNavigationName.serviceDescription,
      StepsNavigationName.weaponData,
      StepsNavigationName.personalAndPassport,
      StepsNavigationName.currentPlaceOfResidence,
      serviceCode === SERVICE_CODES.RESIDENCE_RELOCATION
        ? StepsNavigationName.newAddressOfResidence
        : StepsNavigationName.newTemporaryAddressOfResidence,
      StepsNavigationName.additionalDocument,
      StepsNavigationName.confirmationOfApplication,
      StepsNavigationName.signUp,
      StepsNavigationName.result,
    ]);

    setMyObjects(navigationData);
    totalSteps = navigationData.length;
    stepNumberToSubmitForm = navigationData.length - 1;
    stepNumberToDownloadPdf = navigationData.length - 2;
  }, [serviceCode]);

  return (
    <section className="section-block-medium section-light">
      <div className="container container-with-aside_margin-bottom-sm">
        <HeaderWithHomeLinkButton header={serviceName} />
      </div>
      <div className="wpp-container container container-with-two-asides container-with-aside_gap-md">
        <aside className="container-with-aside__aside">
          <StepsNavigationLegacy navigationData={myObjects} />
        </aside>
        <div>
          <section
            className="container-with-aside__section container-with-aside__section_width-md max-width-md"
            ref={htmlRef}
          >
            <FormSteps onReceiveRequestId={handleReceiveRequestId} />
          </section>

          <ConditionView if={isLastStep && !!currentRequestId}>
            <ServiceFeedback requestId={currentRequestId} />
          </ConditionView>
        </div>
        <ServiceInformerContainer>
          {isClueInformerActive && <ServiceInformer type={ServiceInformerType.CLUE} />}
          {isValidityPeriodViolationInformerActive && (
            <ServiceInformer type={ServiceInformerType.VALIDITY_PERIOD_VIOLATION} />
          )}
          {isPlaceOfResidenceInformerActive && <ServiceInformer type={ServiceInformerType.PLACE_OF_RESIDENCE} />}
          {isQrCodeInformerActive && <ServiceInformer type={ServiceInformerType.QR_CODE1} />}
          <ServiceInformer type={ServiceInformerType.CONTACT_CENTER} />
        </ServiceInformerContainer>
      </div>
    </section>
  );
};
